/**
 * Chat-API base URLs — the ONE place they are resolved (#3219).
 *
 * Before this module, VITE_CHAT_API_URL fallbacks were hardcoded in 21 files
 * with FOUR mutually incompatible defaults — including three files defaulting
 * to port 8001, which is the SIMULATOR, not the chat API (chat-api runs on
 * 8000 locally, see CLAUDE.md), and one service using the /api/chat URL as a
 * root base (shape bug). A misconfigured build answered differently depending
 * on which file you hit.
 *
 * Contract:
 *  · VITE_CHAT_API_URL is injected by both deploy workflows at build time and
 *    set in .env.local / .env.example for dev — it is ALWAYS expected.
 *  · PROD build without it → throw at module load (fail loud at startup, not
 *    by silently talking to the wrong backend from a production bundle).
 *  · DEV build without it → console.warn + localhost:8000 (the chat-api port).
 *  · The env value may be either the root (https://chat-test.zentrian.io) or
 *    the /api/chat URL — both shapes exist in the wild; we normalize once.
 *
 * Exports:
 *  · CHAT_API_URL  — always WITH the /api/chat suffix (chat endpoints).
 *  · CHAT_API_ROOT — always WITHOUT it (sibling routers: /api/model-eval,
 *    /api/alarms, /api/discovery, …).
 *  · MODEL_EVAL_URL — VITE_MODEL_EVAL_URL override or CHAT_API_ROOT-derived.
 */

const RAW = import.meta.env.VITE_CHAT_API_URL as string | undefined;

/** Strip trailing slashes and a trailing /api/chat, in either order. */
function normalizeRoot(value: string): string {
  return value.replace(/\/+$/, '').replace(/\/api\/chat$/, '');
}

function resolveRoot(): string {
  if (RAW && RAW.trim() !== '') {
    return normalizeRoot(RAW.trim());
  }
  if (import.meta.env.PROD) {
    // Both CI workflows inject VITE_CHAT_API_URL as a build arg — a prod
    // bundle without it is a broken build, and silently defaulting would
    // point real users at a test backend. Fail loud, at startup.
    throw new Error(
      '[config/chatApi] VITE_CHAT_API_URL is not set in a production build. ' +
        'Set it as a build arg (see .gitea/workflows/frontend-deploy.yaml).',
    );
  }
  // Dev fallback: the chat-api local port per CLAUDE.md (8000 — NOT 8001,
  // which is the simulator).
  console.warn(
    '[config/chatApi] VITE_CHAT_API_URL is not set — falling back to http://localhost:8000',
  );
  return 'http://localhost:8000';
}

/** Chat-API root, no trailing slash, no /api/chat suffix. */
export const CHAT_API_ROOT: string = resolveRoot();

/** The chat endpoint base — root + /api/chat. */
export const CHAT_API_URL: string = `${CHAT_API_ROOT}/api/chat`;

/** Model-eval router base (own env override, else derived from the root). */
export const MODEL_EVAL_URL: string = (() => {
  const raw = import.meta.env.VITE_MODEL_EVAL_URL as string | undefined;
  if (raw && raw.trim() !== '') return raw.trim().replace(/\/+$/, '');
  return `${CHAT_API_ROOT}/api/model-eval`;
})();

/** Exported for unit tests only. */
export const __internal = { normalizeRoot };
